import type { AISessionPlan, EquipmentKind, Limitation } from '@cadencia/contracts';
import { findExercise } from '../catalog/exercises.js';
import { exerciseAllowed } from '../training/limitations.js';
import { SAFETY_LIMITS } from '../safety/limits.js';
import type { SessionTemplate, SlotRole } from '../training/block-builder.js';
import { findSubstitutions } from '../training/substitution.js';

/**
 * Validacion del PLAN DE SESIONES que propone la IA
 * (§7 y §9 del encargo, docs/AI_ARCHITECTURE.md §8 pasos 3 y 4).
 *
 * Convierte lo que el modelo devuelve en `SessionTemplate[]`, que es el mismo
 * dato que produce `splits.ts` y que `buildBlock` ya sabe consumir. Esa es toda
 * la idea: el motor determinista no se toca, se le da otra plantilla.
 *
 * POR QUE AQUI SE RECHAZA ENTERO Y EN `block-proposal.ts` SE RECORTA
 *
 * La regla ya estaba escrita en `block-proposal.ts` y esto la respeta: se
 * recorta lo que es una lista de numeros independientes y se rechaza entero lo
 * que es un conjunto.
 *
 *   - El volumen por musculo son numeros sueltos. Que el pectoral lleve 16
 *     series no depende de que el gemelo lleve 8, asi que recortar el que se
 *     pasa y dejar los demas es correcto.
 *   - Una SEMANA DE ENTRENAMIENTO es un conjunto. Las sesiones se disenan unas
 *     en funcion de otras: la frecuencia por musculo, el reparto de patrones y
 *     el descanso entre sesiones que comparten musculo salen de mirarlas
 *     juntas. Quedarse con cuatro de seis sesiones no da «un plan mas corto»,
 *     da un plan que no ha disenado nadie —el pectoral dos veces y la espalda
 *     ninguna, por ejemplo— y ademas plausible, que es lo peor.
 *
 * Asi que un plan con un solo defecto estructural se cae ENTERO y el bloque se
 * construye con la plantilla determinista, que es una respuesta valida y sabida
 * buena. `motivos` dice por que, y se registra: cuantas veces se cae dice algo
 * sobre el prompt que no se puede saber de otra forma.
 */

/** Tope de sesiones de fuerza por semana. Fuente unica: `safety/limits.ts`. */
export const MAX_SESIONES = SAFETY_LIMITS.session.maxStrengthSessionsPerWeek;

/**
 * Alternativas que se anaden detras de cada eleccion del modelo.
 *
 * NO es desconfianza hacia el modelo: el hueco de `splits.ts` tambien lleva una
 * lista ordenada, y por el mismo motivo. Un `SessionTemplate` sobrevive al
 * bloque entero y `ensureWeekMaterialized` lo vuelve a resolver cada semana
 * contra el gimnasio de ESE momento; con un solo candidato, quitar una maquina
 * a mitad de bloque deja el hueco vacio para el resto del bloque. Con
 * alternativas, la semana siguiente sale completa.
 *
 * Tres, como `findSubstitutions` por defecto. Mas no ayuda: a partir de ahi la
 * puntuacion cae por debajo de `MIN_SUBSTITUTION_SCORE` y lo que se anadiria ya
 * no es el mismo ejercicio con otro nombre.
 */
const ALTERNATIVAS_POR_HUECO = 3;

export interface SessionPlanInput {
  /**
   * `| undefined` a proposito, aunque el esquema Zod lo declare obligatorio.
   *
   * La cache de aplicacion guarda respuestas SIETE DIAS (`DIAS_DE_CACHE`), asi
   * que una propuesta pedida antes de que existiera este campo puede llegar
   * aqui sin el. Y mas en general: esta funcion es una frontera del dominio y
   * una frontera que revienta con `undefined` obliga a cada llamador a
   * comprobarlo, hasta que uno se olvida. Sin plan, se rechaza; que es
   * exactamente lo que hay que hacer con un plan que no existe.
   */
  proposal: readonly AISessionPlan[] | undefined;
  /** Equipamiento real del gimnasio. Decide que alternativas existen. */
  availableEquipmentKinds: readonly EquipmentKind[];
  /**
   * Sesiones que el atleta declaró poder hacer (docs/TEST_INICIAL.md §2.3).
   * Si está, el plan tiene que traer EXACTAMENTE esas: tres sesiones para
   * quien tiene cinco días desaprovecha dos, y cinco para quien tiene tres no
   * se pueden hacer. Ausente = un perfil anterior al test, y vale lo de antes.
   */
  expectedSessions?: number | undefined;
  /**
   * Limitaciones del atleta. El catálogo que se le ofrece a la IA ya viene sin
   * lo que vetan, así que un ejercicio vetado aquí significa que el modelo se
   * salió de la lista: se rechaza entero, como un ejercicio inventado.
   */
  limitations?: readonly Limitation[] | undefined;
  /**
   * Los slugs que se enviaron en ESTA petición (el catálogo ya filtrado por
   * gimnasio y limitaciones). Si está, un ejercicio fuera de él tumba el plan,
   * aunque exista en el catálogo global: hasta el 23/09/2026 bastaba con que
   * existiera, y `buildBlock` lo sustituía después en silencio por otro que
   * sí cabía, así que el plan aplicado no era el que se había explicado.
   * Las alternativas que se añaden detrás de cada hueco salen de aquí también.
   */
  allowedSlugs?: ReadonlySet<string> | undefined;
}

export interface SessionPlanVerdict {
  /** `null` = se rechaza entero y manda la plantilla determinista. */
  sessions: SessionTemplate[] | null;
  /** Por que se rechazo, en vocabulario del dominio. Vacio si se acepto. */
  motivos: string[];
}

const ROLES: readonly SlotRole[] = ['main', 'secondary', 'accessory'];

export function validateSessionPlan(input: SessionPlanInput): SessionPlanVerdict {
  const motivos: string[] = [];
  const plan = input.proposal ?? [];

  if (plan.length === 0) {
    return { sessions: null, motivos: ['el plan no trae ninguna sesión'] };
  }
  if (plan.length > MAX_SESIONES) {
    motivos.push(`${plan.length} sesiones por semana, el máximo es ${MAX_SESIONES}`);
  }
  if (input.expectedSessions !== undefined && plan.length !== input.expectedSessions) {
    motivos.push(
      `${String(plan.length)} sesiones por semana, y el atleta puede entrenar ${String(input.expectedSessions)}`,
    );
  }

  // Los nombres tienen que ser distintos, y no es cosmetico: al rematerializar
  // una semana, `exercisesOfBlock` recupera los ejercicios ya elegidos POR
  // NOMBRE de sesion. Dos «Torso A» comparten esa lista y se pisan los
  // ejercicios entre ellas a partir de la semana 2.
  const nombres = new Set<string>();
  for (const sesion of plan) {
    const clave = sesion.name.trim().toLowerCase();
    if (nombres.has(clave)) motivos.push(`dos sesiones se llaman «${sesion.name}»`);
    nombres.add(clave);
  }

  const sessions: SessionTemplate[] = [];

  for (const [indice, sesion] of plan.entries()) {
    const vistos = new Set<string>();

    for (const hueco of sesion.exercises) {
      if (findExercise(hueco.exerciseSlug) === undefined) {
        // Un ejercicio inventado no se puede sustituir por «el mas parecido»:
        // no hay nada a lo que parecerse, y ademas significa que la sesion se
        // diseño contra un catalogo que no es el nuestro.
        motivos.push(`ejercicio desconocido en «${sesion.name}»: ${hueco.exerciseSlug}`);
        continue;
      }
      if (input.allowedSlugs !== undefined && !input.allowedSlugs.has(hueco.exerciseSlug)) {
        motivos.push(
          `«${sesion.name}» propone ${hueco.exerciseSlug}, que no estaba en el catálogo de esta petición`,
        );
      }
      const ejercicio = findExercise(hueco.exerciseSlug);
      if (ejercicio !== undefined && input.limitations !== undefined) {
        const veredicto = exerciseAllowed(ejercicio, input.limitations);
        if (!veredicto.allowed) {
          motivos.push(`«${sesion.name}» propone ${hueco.exerciseSlug}, que ${veredicto.reasons.join('; ')}`);
        }
      }
      if (vistos.has(hueco.exerciseSlug)) {
        motivos.push(`«${sesion.name}» repite ${hueco.exerciseSlug}`);
      }
      vistos.add(hueco.exerciseSlug);
    }

    sessions.push({
      name: sesion.name.trim(),
      objective: sesion.objective.trim(),
      orderInWeek: indice,
      slots: sesion.exercises
        .filter((hueco) => findExercise(hueco.exerciseSlug) !== undefined)
        .map((hueco) => ({
          role: ROLES.includes(hueco.role) ? hueco.role : 'accessory',
          candidates: [
            hueco.exerciseSlug,
            ...findSubstitutions(
              hueco.exerciseSlug,
              { availableKinds: input.availableEquipmentKinds },
              ALTERNATIVAS_POR_HUECO,
            )
              .map((alternativa) => alternativa.slug)
              .filter((slug) => input.allowedSlugs === undefined || input.allowedSlugs.has(slug)),
          ],
        })),
    });
  }

  if (motivos.length > 0) return { sessions: null, motivos };
  return { sessions, motivos: [] };
}
